import { LeftOutlined } from '@ant-design/icons'
import { IM_DFM_URL_PARAM } from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/constants'
import ItemNotFound from '@Builder/QuoteDetailPage/ItemNotFound'
import {
    CUSTOMS_TAB,
    MANUFACTURABILITY_FEEDBACK_TAB,
    NAME,
    ORDER_STATUS_TAB,
    PLACEMENT,
    SHIPMENTS_TAB,
} from '@Orders/constants'
import {
    FICTIV_SHIPPING_UPGRADE_PROGRAM_LINK,
    formatDate,
    isAllHtsCodesSubmitted,
    shipDateBeforeHtsDate,
    useOrderDetailData,
} from '@Orders/logic'
import { BORDER } from '@User/MyAccountPage/styleConstants'
import { Alert, Breadcrumb, Card, Skeleton } from 'antd'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useLocation, useNavigate } from 'react-router-dom'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'

import BannerBundle from '~/apps/consumer/components/BannerBundle'
import QuoteOrderTeamName from '~/apps/consumer/pages/Teams/QuoteOrderTeamName'
import Box from '~/core/Box'
import Link from '~/core/Link'
import LoadingSpinner from '~/core/LoadingSpinner'
import Text from '~/core/Text'
import getColor from '~/lib/getColor'
import { useFlagEnabled } from '~/lib/isFlagEnabled'
import { CONFIG_OPTION_EMPTY } from '~/lib/partsConstants'
import { openChatDriftOrderDetails } from '~/redux-modules/drift'
import { getUserIdentity } from '~/redux-modules/identity'
import { ADMIN_PATH, ORDERS_PATH, TEAM_PATH } from '~/redux-modules/preferences'
import { useGetOboDetails } from '../../Quotes/Admin/AdminQuotingPage/components/PartDFMReportPanel/logic'

import { UpdatedHTSTermModal } from './components'
import { OrderContext } from './context'
import ClearanceBanner from './HTS/components/ClearanceBanner'
import {
    getLocalStorageItem,
    ORDER_TYPE,
    STORAGE_ITEM_CHECKED_5106,
} from './HTS/HTSLogic'
import {
    getDisplayClearanceBanner,
    getIsInternational,
    getShouldShowUpdatedHTSTermModal,
    HTS_TYPE,
    isOrderTypeIm,
    shipmentIdsMapType,
    useGetDefaultActiveKey,
} from './logic'
import OrderDetailHeader from './OrderDetailHeader'
import OrderDetails from './OrderDetailHeaderAndFooter/OrderDetails'
import OrderSummaryFooter from './OrderDetailHeaderAndFooter/OrderSummaryFooter'
import OrderSummaryHeader from './OrderDetailHeaderAndFooter/OrderSummaryHeader'
import {
    salesOrder_salesOrder_shipments as SalesOrderShipments,
    salesOrder_salesOrder_shipments_items as SalesOrderShipmentsItems,
} from './Queries/__generated__/salesOrder'
import { selectFlags } from './selectors'
import Shipments from './Shipments'
import styles from './styles.css'

const INCOTERMS_CUSTOMER_ALERT =
    '    Fictiv is the Importer of Record for this order.'

type Params = {
    orderName: string
    tabs: string
}

type OrderDetailsPagePropsType = {
    isLocatedInAdminPage?: boolean
    userId?: number
    isLocatedInTeamPage?: boolean
    teamId?: number
}

function OrderDetailsPage(props: OrderDetailsPagePropsType) {
    const { teamId } = useParams()
    const { isLocatedInAdminPage, isLocatedInTeamPage, userId } = props

    let timeout: NodeJS.Timeout

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { hash } = location

    const { orderName = '', tabs } = useParams<Params>()
    const identity = useSelector(getUserIdentity)
    const {
        isNotifyIncotermsChangeEnabled,
        isImShipmentsEnabled,
        isImToolingEnabled,
    } = useSelector(selectFlags)

    const [understandTerms, setUnderstandTerms] = useState(false)
    const [updatedHTSTermModalVisible, setUpdatedHTSTermModalVisible] =
        useState(false)

    const { data, loading, error, refetch } = useOrderDetailData(orderName)
    const salesOrder = data?.salesOrder
    const quoteType = salesOrder?.sourceQuoteType || null
    const isOrderInErp = salesOrder && !salesOrder?.id.includes(ORDER_TYPE.ERP)
    const orderedById = salesOrder?.paymentDetails?.paidBy?.id

    const isCpqFlagV2Enabled = useFlagEnabled(
        PROVISIONED_UI_FLAGS.UI_CPQ_QUOTES_INTEGRATION_V2,
    )

    const orderContextValue = {
        isImOrder: isOrderTypeIm(quoteType),
        isImReorder: salesOrder?.isImReorder,
    }

    const { isObo } = useGetOboDetails(
        data?.salesOrder?.quoteOwnerUser?.id || '',
    )

    const salesOrderLines =
        isImToolingEnabled && orderContextValue.isImOrder
            ? salesOrder?.salesOrderLines?.parts
            : salesOrder?.salesOrderLines?.items

    const allHtsSubmitted =
        !!salesOrderLines &&
        isAllHtsCodesSubmitted(salesOrderLines, isOrderTypeIm(quoteType))
    const isInternational = (data && getIsInternational(data)) || false

    const defaultTab = useGetDefaultActiveKey({ salesOrderLines })
    const checkForDefaultKey =
        isCpqFlagV2Enabled && salesOrder?.quote
            ? defaultTab
            : isInternational && !allHtsSubmitted
            ? CUSTOMS_TAB
            : defaultTab
    const [selectedTab, setSelectedTab] = useState<string>(checkForDefaultKey)

    const [shipmentIdsMap, setShipmentIdsMap] = useState<shipmentIdsMapType>([])
    const [htsType, setHtsType] = useState<string | null>(null)

    let defaultBackToOrdersLink = ORDERS_PATH

    if (isLocatedInTeamPage && teamId) {
        defaultBackToOrdersLink =
            isLocatedInAdminPage && userId
                ? `${ADMIN_PATH}/user/${userId}/team/${teamId}${ORDERS_PATH}`
                : `${TEAM_PATH}/${teamId}${ORDERS_PATH}`
    } else if (isLocatedInAdminPage && userId) {
        defaultBackToOrdersLink = `${ADMIN_PATH}/user/${userId}${ORDERS_PATH}`
    }

    const [backToOrdersLink, setBackToOrdersLink] = useState(
        defaultBackToOrdersLink,
    )

    const item = getLocalStorageItem(`${STORAGE_ITEM_CHECKED_5106}`)
    const [checked5106, setChecked5106] = useState(item === 'true')

    const { id: userInfoId, isAdmin, loading: identityLoading } = identity

    const shouldShowUpdatedHTSTermModal = getShouldShowUpdatedHTSTermModal({
        isInternational,
        createdAt: salesOrder?.createdAt,
    })

    useEffect(() => {
        if (
            !isObo &&
            !allHtsSubmitted &&
            !updatedHTSTermModalVisible &&
            !understandTerms &&
            shouldShowUpdatedHTSTermModal
        ) {
            setUpdatedHTSTermModalVisible(true)
        }
    }, [
        updatedHTSTermModalVisible,
        shouldShowUpdatedHTSTermModal,
        understandTerms,
        isObo,
        allHtsSubmitted,
    ])
    const url = hash.replace('#', '')

    useEffect(() => {
        if (
            tabs === IM_DFM_URL_PARAM ||
            url === MANUFACTURABILITY_FEEDBACK_TAB
        ) {
            setSelectedTab(MANUFACTURABILITY_FEEDBACK_TAB)
        } else if (url === ORDER_STATUS_TAB) {
            setSelectedTab(ORDER_STATUS_TAB)
        } else if (url === SHIPMENTS_TAB) {
            setSelectedTab(SHIPMENTS_TAB)
        } else if (url === CUSTOMS_TAB) {
            setSelectedTab(CUSTOMS_TAB)
        } else {
            setSelectedTab(checkForDefaultKey)
        }
    }, [checkForDefaultKey, tabs, url])

    useEffect(() => {
        if (!identityLoading && !loading) {
            if (
                isAdmin &&
                orderedById &&
                orderedById !== userInfoId &&
                !(tabs === IM_DFM_URL_PARAM) &&
                !isLocatedInTeamPage
            ) {
                navigate(
                    `${ADMIN_PATH}/user/${orderedById}${ORDERS_PATH}/${salesOrder?.name}`,
                )
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, identityLoading, isAdmin, loading, orderedById, userInfoId])

    useEffect(() => {
        const routeFrom = location?.state?.from || null

        if (routeFrom?.includes?.(ORDERS_PATH)) {
            setBackToOrdersLink(routeFrom)
        }
    }, [location.state])

    const createShipmentsIdsMap = (shipments: SalesOrderShipments) => {
        const shipmentIds = shipments?.items.map(
            (shipment: SalesOrderShipmentsItems, key: number) => ({
                [key]: shipment.id,
            }),
        )

        setShipmentIdsMap(shipmentIds)
    }

    useEffect(() => {
        if (salesOrder?.shipments) {
            createShipmentsIdsMap(salesOrder?.shipments)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salesOrder?.shipments])

    const scheduleRefetch = (until: number) => {
        timeout = setTimeout(async () => {
            if (until < Date.now() || salesOrder?.id.includes(ORDER_TYPE.ERP)) {
                return
            }

            try {
                await refetch?.()
            } finally {
                scheduleRefetch(until)
            }
        }, 10000)
        return () => {
            clearTimeout(timeout)
        }
    }

    useEffect(() => {
        if (isOrderInErp) {
            return scheduleRefetch(Date.now() + 60 * 5 * 1000)
        }
    })

    const message = (
        <Box display="inline">
            <Text style={{ marginLeft: '10px' }}>
                {' '}
                {INCOTERMS_CUSTOMER_ALERT}{' '}
            </Text>
            <Link
                name="fictiv-shipping-upgrade"
                data-test-target="fictiv-shipping-upgrade"
                target="_blank"
                href={FICTIV_SHIPPING_UPGRADE_PROGRAM_LINK}
                placement={PLACEMENT}
            >
                Learn more about Fictiv’s shipping upgrade program.
            </Link>
        </Box>
    )

    if (loading) {
        return <LoadingSpinner />
    }

    const openChat = () => {
        if (!dispatch) {
            return null
        }

        return dispatch(openChatDriftOrderDetails(salesOrder?.name))
    }

    if (!data || (!salesOrder && error)) {
        return <ItemNotFound isQuote={false} />
    }

    const {
        createdAt = '',
        deliveryStatus = '',
        depositAmount = 0,
        fictivEstimatedDeliveryDate = '',
        fictivOrderUpdatedToDdp = {},
        fictivShipmentMethod = '',
        id = '',
        isImReorder = false,
        name = '',
        paymentDetails = null,
        project = null,
        salesOrderLines: { items = [], parts = [] } = {},
        shipments = null,
        shippedBy = '',
        sourceQuoteType = '',
        totalAmount = 0,
        isBusinessAccountDiscountApplied = null,
        appliedBusinessAccountDiscountPercent = null,
        quoteId = '',
        team,
    } = salesOrder || {}
    const billingAddress = paymentDetails?.billingAddress || null

    const showOrderSummaryHeader = createdAt && paymentDetails

    const isImOrder = isOrderTypeIm(sourceQuoteType)
    const records =
        isImOrder && isImToolingEnabled && parts?.length
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              items.concat(parts)
            : items

    const getInitialHTSType = (orderNameParam: string): string =>
        getLocalStorageItem(`${HTS_TYPE}:${orderNameParam}`) ||
        (records && records[0]?.htsUsageType)

    const initialHtsType = getInitialHTSType(name)
    const expectedShipDate =
        formatDate(salesOrder?.shippedBy) || CONFIG_OPTION_EMPTY
    const htsReleaseDateFlag = shipDateBeforeHtsDate(expectedShipDate)

    const displayClearanceBanner = getDisplayClearanceBanner({
        deliveryStatus,
        isInternational,
        htsReleaseDateFlag,
        checked5106,
        allHtsSubmitted,
        htsType: htsType || initialHtsType,
    })

    const onBackToOrdersClick = (evt: Event) => {
        evt.preventDefault()
        if (backToOrdersLink) {
            navigate(backToOrdersLink, { replace: true })
        }
    }

    return (
        <OrderContext.Provider value={orderContextValue}>
            {isCpqFlagV2Enabled && salesOrder?.quotePdfUrl
                ? null
                : updatedHTSTermModalVisible &&
                  !understandTerms && (
                      <UpdatedHTSTermModal
                          visible={updatedHTSTermModalVisible}
                          setUnderstandTerms={setUnderstandTerms}
                          onClose={() => setUpdatedHTSTermModalVisible(false)}
                      />
                  )}
            <Box>
                <Box
                    className={styles.linkButtonWrapper}
                    justifyContent="between"
                >
                    <Breadcrumb className={styles.backToLink}>
                        <Breadcrumb.Item>
                            <Link
                                data-test-target="link-to-orders-page"
                                name={NAME}
                                onClick={onBackToOrdersClick}
                                placement={PLACEMENT}
                                style={{
                                    color: getColor('link'),
                                }}
                            >
                                <LeftOutlined /> Back to all orders
                            </Link>
                        </Breadcrumb.Item>
                    </Breadcrumb>
                </Box>
                <Box direction="col" marginLeft="auto">
                    <Box
                        direction="rowReverse"
                        className={styles.linkButtonWrapper}
                    >
                        <OrderDetailHeader
                            data={data}
                            placement={PLACEMENT}
                            userId={userId}
                            isObo={isObo}
                        />
                    </Box>
                    {team?.name && (
                        <Box marginBottom="16px" justifyContent="end">
                            <QuoteOrderTeamName name={team.name} />
                        </Box>
                    )}
                </Box>
            </Box>
            {!isLocatedInAdminPage && <BannerBundle placement={PLACEMENT} />}
            {isNotifyIncotermsChangeEnabled && fictivOrderUpdatedToDdp && (
                <Box
                    data-test-target="fictiv-incoterms-upgrade"
                    marginBottom="13px"
                    marginTop="3px"
                >
                    <Alert
                        message={message}
                        showIcon
                        type="info"
                        style={{
                            border: 'none',
                            fontSize: '16px',
                            fontWeight: 'normal',
                            marginBottom: '0px',
                            width: '100%',
                        }}
                    />
                </Box>
            )}
            {displayClearanceBanner && (
                <ClearanceBanner
                    alertClassName={styles.clearanceBanner}
                    allHtsSubmitted={allHtsSubmitted}
                    checked5106={checked5106}
                    createdAt={data.salesOrder?.createdAt}
                    openChat={openChat}
                />
            )}
            {data && (
                <Card
                    type="inner"
                    loading={!data || loading || !showOrderSummaryHeader}
                    title={
                        showOrderSummaryHeader ? (
                            <OrderSummaryHeader
                                createdAt={createdAt}
                                depositAmount={depositAmount}
                                isImOrder={isImOrder}
                                name={name}
                                paymentDetails={paymentDetails}
                                project={project}
                                saleOrderId={id}
                                totalAmount={totalAmount}
                                isBusinessAccountDiscountApplied={
                                    isBusinessAccountDiscountApplied
                                }
                                appliedBusinessAccountDiscountPercent={
                                    appliedBusinessAccountDiscountPercent
                                }
                                team={team}
                                salesOrder={salesOrder}
                            />
                        ) : (
                            <div className={styles.loader}>
                                <Skeleton
                                    active
                                    title={{
                                        width: 0,
                                        style: { display: 'none' },
                                    }}
                                    paragraph={{ rows: 1, width: '100%' }}
                                />
                            </div>
                        )
                    }
                    style={{ border: BORDER }}
                    headStyle={{ padding: '4px 24px 0px' }}
                >
                    <OrderDetails
                        isImOrder={isImOrder}
                        paymentDetails={paymentDetails}
                        quoteName={salesOrder?.quote?.name}
                        salesOrder={salesOrder}
                    />
                </Card>
            )}
            <Shipments
                {...{
                    allHtsSubmitted,
                    billingAddress,
                    checked5106,
                    deliveryStatus,
                    fictivEstimatedDeliveryDate,
                    fictivShipmentMethod,
                    htsReleaseDateFlag,
                    htsType,
                    id,
                    initialHtsType,
                    isImOrder,
                    isImReorder,
                    isImShipmentsEnabled,
                    isInternational,
                    isLocatedInAdminPage,
                    location,
                    name,
                    openChat,
                    quoteType,
                    records,
                    selectedTab,
                    setChecked5106,
                    setHtsType,
                    setSelectedTab,
                    shipmentIdsMap,
                    shipments,
                    shippedBy,
                    userId,
                    createdAt,
                    quoteId,
                    sourceQuoteType,
                    salesOrder,
                }}
            />
            <OrderSummaryFooter openChat={openChat} />
        </OrderContext.Provider>
    )
}

export default OrderDetailsPage
